import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DestinationsApiService, DestinationDto, DestinationTypeDto } from '../../../services/destinations-api.service';
import { ClientCrypto } from '../../../utils/client-crypto';
import { firstValueFrom } from 'rxjs';
import { DestinationView } from '../destination.types';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-destinations-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './destinations-form.component.html',
  styleUrls: ['../destinations.component.scss']
})
export class DestinationsFormComponent {
  @Input() destinationTypes: DestinationTypeDto[] = [];
  private _hasGlobalDataScope = false;
  get hasGlobalDataScope(): boolean { return this._hasGlobalDataScope; }
  @Input() set hasGlobalDataScope(value: boolean) {
    this._hasGlobalDataScope = value;
    const ctrl = this.destinationForm?.get('ownerEntitySelect');
    if (value) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }
  @Input() ownerEntityOptions: { value: string; label: string }[] = [];
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  formMode: 'create' | 'edit' = 'create';
  formTitleKey = 'destinations.form.createTitle';
  destinationForm: FormGroup;
  saving = false;
  selectedDestinationType: DestinationTypeDto | null = null;
  connectionForm: FormGroup | null = null;
  connectionFieldDefs: DestinationFieldDefinition[] = [];

  get destinationTypeOptions(): CustomSelectOption[] {
    const options = this.destinationTypes.map(type => ({
      value: String(type.ID),
      label: type.Description || type.Name || type.ActivationType
    }));
    return options;
  }

  constructor(
    private destinationsApi: DestinationsApiService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private crypto: ClientCrypto,
    private authService: AuthService
  ) {
    this.destinationForm = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      destinationTypeId: [null, Validators.required],
      connectionParams: [''],
      active: [true],
      ownerEntitySelect: ['']
    });

    this.destinationForm.get('ownerEntitySelect')?.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((id: string) => {
        if (id) {
          this.destinationForm.patchValue({ ownerEntitySelect: id }, { emitEvent: false });
        }
      });

    this.destinationForm.get('destinationTypeId')?.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        const typeId = typeof value === 'string' ? parseInt(value, 10) : value;
        this.selectedDestinationType = this.destinationTypes.find(type => type.ID === typeId) ?? null;
        this.initializeConnectionForm();
      });
  }

  openCreate(): void {
    this.formMode = 'create';
    this.formTitleKey = 'destinations.form.createTitle';
    this.destinationForm.reset({
      name: '',
      destinationTypeId: null,
      connectionParams: '',
      active: true,
      ownerEntitySelect: ''
    });
    this.selectedDestinationType = null;
    this.connectionForm = null;
    this.connectionFieldDefs = [];
    this.initializeConnectionForm();
  }

  openEdit(item: DestinationDto | DestinationView): void {
    this.formMode = 'edit';
    this.formTitleKey = 'destinations.form.editTitle';
    const destinationTypeId = 'DestinationTypeID' in item ? item.DestinationTypeID : item.destinationTypeId;
    this.selectedDestinationType = this.destinationTypes.find(t => t.ID === destinationTypeId) ?? null;
    const active = 'IsActive' in item ? item.IsActive === 1 : this.isDestinationView(item) ? item.status === 'active' : true;
    const entityId = 'EntityID' in item ? item.EntityID : (item as any).entityId;
    this.destinationForm.reset({
      id: 'ID' in item ? item.ID : item.id,
      name: 'Name' in item ? item.Name : item.name,
      destinationTypeId: String(destinationTypeId),
      connectionParams: '',
      active,
      ownerEntitySelect: entityId || ''
    });
    this.initializeConnectionForm();
  }

  async submit(): Promise<void> {
    if (this.destinationForm.invalid) {
      this.destinationForm.markAllAsTouched();
      this.connectionForm?.markAllAsTouched();
      return;
    }
    if (this.connectionForm && this.connectionFieldDefs.length && this.connectionForm.invalid) {
      this.connectionForm.markAllAsTouched();
      return;
    }
    const formValue = this.destinationForm.getRawValue();
    const typeId = typeof formValue.destinationTypeId === 'string' ? parseInt(formValue.destinationTypeId, 10) : formValue.destinationTypeId;
    const destinationType = this.destinationTypes.find(type => type.ID === typeId);
    if (!destinationType) {
      alert(this.translate.instant('destinations.errors.typeRequired'));
      return;
    }
    let parsedParams: any;
    if (this.connectionForm && this.connectionFieldDefs.length) {
      parsedParams = this.connectionForm.getRawValue();
    } else {
      const rawParams = (formValue.connectionParams || '').toString().trim();
      if (!rawParams) {
        parsedParams = {};
      } else {
        try {
          parsedParams = JSON.parse(rawParams);
        } catch {
          alert(this.translate.instant('destinations.errors.invalidJson'));
          this.saving = false;
          return;
        }
      }
    }

    this.saving = true;
    const entityId = this.hasGlobalDataScope
      ? (formValue.ownerEntitySelect || null)
      : this.resolveCurrentDcrId();
    if (!entityId) {
      alert(this.translate.instant('destinations.errors.ownerRequired'));
      this.saving = false;
      return;
    }

    const payload: Partial<DestinationDto> = {
      ID: formValue.id,
      Name: formValue.name,
      DestinationTypeID: destinationType.ID,
      ActivationType: destinationType.ActivationType,
      ConnectorType: destinationType.ConnectorType,
      EntityID: entityId,
      DestinationTypeName: destinationType.Name,
      IsActive: formValue.active ? 1 : 0
    };

    try {
      const encrypted = await this.crypto.encrypt(JSON.stringify(parsedParams));
      (payload as any).EncryptedConnectionParamsJson = encrypted.encryptedMessage;
      (payload as any).EncryptedKey = encrypted.encryptedKey;
      (payload as any).EncryptedIv = encrypted.encryptedIv;

      if (this.formMode === 'edit') {
        await firstValueFrom(this.destinationsApi.updateDestination(payload));
      } else {
        await firstValueFrom(this.destinationsApi.createDestination(payload));
      }
      this.saved.emit();
      this.openCreate();
    } catch (error: any) {
      console.error('[destinations] save failed', error);
      alert(error?.message || this.translate.instant('destinations.errors.save'));
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    if (this.saving) return;
    this.cancelled.emit();
  }

  private isDestinationView(item: DestinationDto | DestinationView): item is DestinationView {
    return (item as DestinationView).destinationTypeId !== undefined;
  }

  private initializeConnectionForm(): void {
    const connectionControl = this.destinationForm.get('connectionParams');
    const selectedType = this.selectedDestinationType;
    if (!selectedType) {
      this.connectionFieldDefs = [];
      this.connectionForm = null;
      connectionControl?.reset('', { emitEvent: false });
      connectionControl?.clearValidators();
      connectionControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    if (this.formMode === 'edit') {
      this.connectionFieldDefs = [];
      this.connectionForm = null;
      connectionControl?.reset('', { emitEvent: false });
      connectionControl?.clearValidators();
      connectionControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const fieldDefs = this.resolveFieldDefinitions(selectedType);
    this.connectionFieldDefs = fieldDefs;
    if (!fieldDefs.length) {
      this.connectionForm = null;
      connectionControl?.setValidators([Validators.required]);
      connectionControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    const group: Record<string, FormControl> = {};
    fieldDefs.forEach(def => {
      group[def.key] = this.fb.control(
        def.defaultValue ?? '',
        def.required ? [Validators.required] : []
      );
    });
    this.connectionForm = this.fb.group(group);
    connectionControl?.clearValidators();
    connectionControl?.setValue('', { emitEvent: false });
    connectionControl?.updateValueAndValidity({ emitEvent: false });
  }

  get hasDynamicConnectionFields(): boolean {
    return !!this.connectionForm && this.connectionFieldDefs.length > 0;
  }

  getFieldLabel(field: DestinationFieldDefinition): string {
    const key = `destinations.form.fields.${field.key}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return field.label;
  }

  getFieldPlaceholder(field: DestinationFieldDefinition): string | null {
    if (!field.placeholder) {
      return null;
    }
    const key = `destinations.form.placeholders.${field.placeholder}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return field.placeholder;
  }

  getConnectorLabel(connectorTypeId?: number | null): string {
    if (connectorTypeId === null || connectorTypeId === undefined) {
      return '—';
    }
    return CONNECTOR_LABELS[connectorTypeId] || `Connector ${connectorTypeId}`;
  }

  onJsonFileSelected(event: Event, field: DestinationFieldDefinition): void {
    if (!this.connectionForm || !field) {
      return;
    }
    const input = event.target as HTMLInputElement | null;
    const file = input?.files ? input.files[0] : null;
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === 'string'
        ? reader.result.replace(/\r?\n/g, '')
        : '';
      this.connectionForm?.get(field.key)?.setValue(value);
      this.connectionForm?.get(field.key)?.markAsDirty();
      this.connectionForm?.get(field.key)?.markAsTouched();
    };
    reader.readAsText(file);
    if (input) {
      input.value = '';
    }
  }

  private resolveFieldDefinitions(type: DestinationTypeDto | null): DestinationFieldDefinition[] {
    if (!type) {
      return [];
    }
    const activationKey = `activation:${type.ActivationType}`;
    const connectorKey = `connector:${type.ConnectorType}`;
    return DESTINATION_FIELD_CONFIG[activationKey] ||
      DESTINATION_FIELD_CONFIG[connectorKey] ||
      [];
  }

  private resolveCurrentDcrId(): string | null {
    const selected = this.authService.getActiveDcr();
    if (selected) {
      return selected;
    }
    const fallback = this.authService.getAudienceCreationDcrId();
    if (fallback) {
      return fallback;
    }
    const currentUser = this.authService.currentUserValue;
    return currentUser?.entityID ?? null;
  }
}

interface DestinationFieldDefinition {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'password' | 'number';
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
}

const CONNECTOR_LABELS: Record<number, string> = {
  1: 'Facebook',
  2: 'Google Cloud Storage',
  3: 'Pubmatic',
  6: 'Amazon S3',
  9: 'Azure Blob',
  10: 'SFTP',
  11: 'Local GCS',
  13: 'External API',
  14: 'GCS AWS Federated'
};

const DESTINATION_FIELD_CONFIG: Record<string, DestinationFieldDefinition[]> = {
  'connector:10': [
    { key: 'SFTPHost', label: 'SFTP Host', required: true },
    { key: 'SFTPPort', label: 'SFTP Port', required: true },
    { key: 'SFTPUsername', label: 'Username', required: true },
    { key: 'SFTPPassword', label: 'Password', type: 'password', required: true },
    { key: 'SFTPFolder', label: 'Folder', required: true }
  ],
  'connector:6': [
    { key: 'AccessKeyID', label: 'Access Key ID', required: true },
    { key: 'SecretAccessKey', label: 'Secret Access Key', type: 'password', required: true },
    { key: 'AWS_S3_Bucket', label: 'S3 Bucket', required: true },
    { key: 'AWS_S3_Folder', label: 'S3 Folder', required: true },
    { key: 'AWS_S3_Region', label: 'Region', required: true, defaultValue: 'eu-west-1' },
    { key: 'AWS_S3_AssumeRole', label: 'Assume Role' },
    { key: 'AWS_S3_Endpoint', label: 'Custom endpoint' }
  ],
  'connector:9': [
    { key: 'Account', label: 'Account', required: true },
    { key: 'AccountKey', label: 'Account Key', required: true },
    { key: 'StoregeContainer', label: 'Storage container', required: true },
    { key: 'SasToken', label: 'SAS token', required: true }
  ],
  'activation:Neodata': [
    { key: 'Account', label: 'Account', required: true },
    { key: 'AccountKey', label: 'Account Key', required: true },
    { key: 'StoregeContainer', label: 'Storage container', required: true },
    { key: 'SasToken', label: 'SAS token', required: true },
    { key: 'NeodataCompanyID', label: 'Company ID', required: true },
    { key: 'NeodataPartnerID', label: 'Partner ID', required: true }
  ],
  'connector:2': [
    { key: 'JsonCredentials', label: 'JSON credentials', type: 'textarea', required: true },
    { key: 'InputBucket', label: 'Input bucket', required: true },
    { key: 'AllowedGoogleGroup', label: 'Allowed Google group' },
    { key: 'ServiceAccount', label: 'Service account' }
  ],
  'connector:11': [
    { key: 'JsonCredentials', label: 'JSON credentials', type: 'textarea' },
    { key: 'InputBucket', label: 'Input bucket', required: true }
  ],
  'activation:GAM': [
    { key: 'AdManagerNetworkCode', label: 'Ad Manager network code', required: true },
    { key: 'JsonCredentials', label: 'JSON credentials', type: 'textarea', required: true },
    { key: 'InputBucket', label: 'Input bucket', required: true },
    { key: 'ResultBucket', label: 'Result bucket', required: true }
  ],
  'connector:14': [
    { key: 'AWS_ACCESS_KEY_ID', label: 'AWS Access Key ID', required: true },
    { key: 'AWS_SECRET_ACCESS_KEY', label: 'AWS Secret', required: true },
    { key: 'AWS_DEFAULT_REGION', label: 'AWS Region', required: true },
    { key: 'PARTNER_ID', label: 'Partner ID', required: true },
    { key: 'PROJECT_ID', label: 'Project ID', required: true },
    { key: 'PROJECT_NUMBER', label: 'Project number', required: true },
    { key: 'BUCKET_NAME', label: 'Bucket name', required: true },
    { key: 'POOL_ID', label: 'Pool ID', required: true },
    { key: 'GCS_FOLDER', label: 'Folder' }
  ],
  'connector:3': [
    { key: 'Username', label: 'Username', required: true },
    { key: 'Password', label: 'Password', type: 'password', required: true },
    { key: 'UIToken', label: 'UI token' },
    { key: 'AccountID', label: 'Account ID', required: true },
    { key: 'DataProviderID', label: 'Data provider ID', required: true },
    { key: 'APIProduct', label: 'API product', required: true },
    { key: 'SFTPHost', label: 'SFTP Host', required: true },
    { key: 'SFTPPort', label: 'SFTP Port', required: true },
    { key: 'SFTPUsername', label: 'SFTP Username', required: true },
    { key: 'SFTPPassword', label: 'SFTP Password', type: 'password', required: true },
    { key: 'SFTPFolder', label: 'SFTP Folder', required: true }
  ],
  'activation:TheTradeDesk': [
    { key: 'ProviderID', label: 'Provider ID', required: true },
    { key: 'SecretKey', label: 'Secret Key', type: 'password', required: true },
    { key: 'ParentElementID', label: 'Parent Element ID', required: true },
    { key: 'TTDAuthKey', label: 'TTD Auth Key', required: true },
    { key: 'BrandId', label: 'Brand ID', required: true }
  ],
  'activation:Tealium': [
    { key: 'Account', label: 'Account', required: true },
    { key: 'Profile', label: 'Profile', required: true },
    { key: 'Datasource', label: 'Datasource' }
  ],
  'activation:AdformAPI': [
    { key: 'ClientID', label: 'Client ID', required: true },
    { key: 'ClientSecret', label: 'Client Secret', type: 'password', required: true }
  ],
  'activation:FB': [
    { key: 'id', label: 'External ID', required: true },
    { key: 'name', label: 'Account name', required: true },
    { key: 'accessToken', label: 'Access token', type: 'textarea', required: true },
    { key: 'userID', label: 'User ID', required: true },
    { key: 'accessTokenExpireTimestamp', label: 'Token expiration' }
  ]
};
